import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";
import MerchandisingListing from "app/pages/Merchandising/MerchandisingListing/MerchandisingListing";
import { forceCheck } from "react-lazyload";
import { useLocation } from "react-router-dom";
import { acceptCookyPolicy } from "app/pages/.shared/CookiePolicyFooter/cookiePolicyActionCreators";
import { clearProduct } from "app/pages/Listing/productActionCreators";
import {
	getMerchandisingMedias,
	getMerchandisingTopicsFilterValuesFromMerchCode,
	getProductsOfMerchandising,
	shouldMerchHideProductPriceBanner,
} from "app/pages/Merchandising/merchandisingSelector";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { fetchMerchandising } from "app/pages/Merchandising/merchandisingActionCreators";
import { parseQueries } from "app/utils/routerUtils";
import {
	getFallbackRedirectUrl,
	getPartnerListingSEO,
	getPaymentDescriptionBlock,
	getWebsiteTunnels,
} from "app/reducers/partnerSelector";
import { resetAllProductsFilter } from "app/pages/Listing/ProductFilters/productsFilterActionCreators";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { mapProductToTag } from "app/utils/analytics-store-mapper";
import { clearBooking, updateSDPFormDestination } from "app/pages/Booking/bookingActionCreators";
import {
	HTTP_STATUS_CODES,
	PRODUCTS_FILTERS_KEY,
	PRODUCT_EXPERIENCES,
	PRODUCT_TAG_CODES,
	MULTI_STAGE_JOURNEY_OPTIONS,
	OFFER_TYPE_LABELS,
} from "app/constants";
import { redirectToShopUrl } from "app/utils/utils";
import { FilterProvider, OPTION_MODE } from "app/features/product/productFilters";
import { useIntl } from "react-intl";
import { fetchSDPFilters } from "app/pages/SmartDP/smartDPActionCreators";
import { SortProvider } from "app/features/product/productSort";

const CUSTOM_COMPARATORS = {
	recommendation: (a, b) => (a?.rank || 0) - (b?.rank || 0),
	price_asc: (a, b) => (a?.fromPriceType?.value ?? 0) - (b?.fromPriceType?.value ?? 0),
	price_desc: (a, b) => (b?.fromPriceType?.value ?? 0) - (a?.fromPriceType?.value ?? 0),
};

const MerchandisingContainer = props => {
	const {
		acceptCookyPolicy,
		fetchMerchandising,
		currentMerchandising,
		resetAllProductsFilter,
		updateSDPFormDestination,
		fetchSDPFilters = () => {},
		clearBooking,
		currentShop,
		destinations = [],
		productsOfMerchandising,
	} = props;
	const { formatMessage, formatDate } = useIntl();
	const [loading, setLoading] = useState(true);
	const location = useLocation();
	const queries = parseQueries(location.search);

	const { track } = useAnalytics();

	const { shop, productsVisibility, partnerCode, envVars } = useContext(AppGlobalsContext);

	const proxyBasenamePrefix = envVars?.PROXY_BASENAME_PREFIX;

	useEffect(() => {
		fetchSDPFilters();
	}, []);

	useEffect(() => {
		resetAllProductsFilter();
		clearBooking();

		fetchMerchandising({
			code: queries.code,
			shop,
			productsVisibility,
			partnerCode,
		}).then(response => {
			if (response?.status !== HTTP_STATUS_CODES.OK) {
				setLoading(false);
				const [currentLanguageCode, currentCountryCode] = currentShop.split("-");
				redirectToShopUrl({
					language: currentLanguageCode,
					country: currentCountryCode,
					proxyBasenamePrefix,
				});
			}

			const merchandising = get(response, "data.merchandising", {});
			const products = get(response, "data.products", []);
			setLoading(false);

			track("merchandising_product_list", {
				code: merchandising.code,
				label: merchandising.label,
				position: String(merchandising.position),
				indexCarousel: String(merchandising.headerIndex),
				products: products
					.slice(0, 3)
					.map(mapProductToTag)
					.map((product, position) => ({ ...product, position })),
				merchCode: merchandising.code,
				isMerchMedia: String(!isEmpty(merchandising.media)),
				smartDP: !isEmpty(merchandising.displayOptions?.smartDP)
					? {
							title: merchandising.displayOptions?.smartDP?.merchandisingTitle,
							subtitle: merchandising.displayOptions?.smartDP?.merchandisingSubtitle,
					  }
					: undefined,
			});

			forceCheck();
		});

		return () => {
			acceptCookyPolicy();
		};
	}, [queries.code]);

	const merchDestinationResort = currentMerchandising?.destinationFilterValues
		? destinations.find(
				destination =>
					destination.label === currentMerchandising?.destinationFilterValues[0]
		  )
		: undefined;

	useEffect(() => {
		const hideSmartDPForm = currentMerchandising?.displayOptions?.smartDP?.hideSmartDPForm;

		if (
			!hideSmartDPForm &&
			currentMerchandising?.destinationFilterValues?.length === 1 &&
			merchDestinationResort
		) {
			updateSDPFormDestination({
				code: merchDestinationResort.code.toString(),
				label: merchDestinationResort.label,
			});
		}
	}, [currentMerchandising?.code, merchDestinationResort?.code]);

	const allCountries = [...new Set(productsOfMerchandising?.map(p => p.country).filter(Boolean))];
	const destinationGetter = allCountries.length > 1 ? item => item.country : item => item.resort;
	const shouldShowMultiStageJourneyOption = productsOfMerchandising?.some(product =>
		[
			PRODUCT_EXPERIENCES.CIRCUIT,
			PRODUCT_EXPERIENCES.AUTOTOUR,
			PRODUCT_EXPERIENCES.CRUISE,
		].includes(product?.productExperience)
	);

	const facetConfigs = useMemo(
		() => [
			{
				field: PRODUCTS_FILTERS_KEY.DESTINATION,
				title: formatMessage({ id: "product.page.filter.destinations.label" }),
				valueGetter: destinationGetter,
				...(shouldShowMultiStageJourneyOption && {
					buildOptions: () => {
						return [
							{
								value: "multi-stage-journey",
								label: formatMessage({
									id: "listing.filter.destination.multi.destinations.label",
								}),
							},
						];
					},
					customMatcher: product => {
						return [
							PRODUCT_EXPERIENCES.CIRCUIT,
							PRODUCT_EXPERIENCES.AUTOTOUR,
							PRODUCT_EXPERIENCES.CRUISE,
						].includes(product?.productExperience);
					},
				}),
				allowSearch: true,
				search: { placeholderKey: "filter.destination.searchbar.placeholder" },
			},
			{
				field: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
				title: formatMessage({ id: "product.page.filter.offer.type.label" }),
				valueGetter: product => product.merchandisings,
				buildOptions: products => {
					const offerTypes = [
						...new Set(
							products
								.flatMap(product => product.merchandisings)
								.map(merchandising => {
									if (
										merchandising?.startsWith(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									) {
										return PRODUCT_TAG_CODES.COUP_DE_COEUR;
									}
									if (merchandising?.startsWith(PRODUCT_TAG_CODES.GC)) {
										return PRODUCT_TAG_CODES.GC;
									}
									return null;
								})
								.filter(Boolean)
						),
					];

					return offerTypes.map(merchandising => {
						return {
							value: merchandising,
							labelKey: OFFER_TYPE_LABELS[merchandising] ?? merchandising,
						};
					});
				},
				customMatcher: (product, selectedValues) => {
					const productOfferTypes = product.merchandisings;

					return productOfferTypes?.some(offerType =>
						selectedValues?.some(selectedValue => offerType.startsWith(selectedValue))
					);
				},
				allowSingleOption: true,
				optionMode: OPTION_MODE.MANUAL_ONLY,
			},
			{
				field: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
				title: formatMessage({ id: "multi.stage.journey" }),
				valueGetter: product => product.productExperience,
				buildOptions: products => {
					return [...new Set(products.map(product => product.productExperience))]
						.filter(option => Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(option))
						.map(option => ({
							value: option,
							label: formatMessage({ id: MULTI_STAGE_JOURNEY_OPTIONS[option] }),
						}));
				},
				optionMode: OPTION_MODE.MANUAL_ONLY,
				allowSingleOption: true,
			},
			{
				field: PRODUCTS_FILTERS_KEY.TOPIC,
				title: formatMessage({ id: "listing.filter.theme.label" }),
				valueGetter: item => item.topics,
			},
			{
				field: PRODUCTS_FILTERS_KEY.MONTH,
				title: formatMessage({ id: "listing.filter.month.label" }),
				valueGetter: item => item.months?.map(month => month.label),
				formatLabel: ({ label }) => {
					const [year, month] = label?.split("/");
					const date = new Date(Number(year), Number(month) - 1, 1);
					return formatDate(date, { month: "long", year: "numeric" });
				},
				customSort: (a, b) => {
					return a?.value?.localeCompare(b?.value);
				},
			},
			{
				field: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
				title: formatMessage({ id: "product.page.filter.property.name.label" }),
				allowSearch: true,
				search: { placeholderKey: "filter.property.name.searchbar.placeholder" },
				valueGetter: product => product.id,
				buildOptions: products => {
					return products
						.filter(
							product =>
								!Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(
									product?.productExperience
								)
						)
						.map(product => ({ value: product.id, label: product.name }))
						.sort((a, b) => a.label.localeCompare(b.label));
				},
				optionMode: OPTION_MODE.MANUAL_ONLY,
				showCount: false,
			},
		],
		[destinationGetter, shouldShowMultiStageJourneyOption]
	);

	return (
		<FilterProvider products={productsOfMerchandising} facetConfigs={facetConfigs}>
			<SortProvider comparators={CUSTOM_COMPARATORS}>
				<MerchandisingListing {...props} loading={loading} />
			</SortProvider>
		</FilterProvider>
	);
};

MerchandisingContainer.propTypes = {
	clearProduct: PropTypes.func,
	acceptCookyPolicy: PropTypes.func,
	resetAllProductsFilter: PropTypes.func,
	fetchSDPFilters: PropTypes.func,
	partnerCode: PropTypes.string,
	resolution: PropTypes.string,
	fetchMerchandising: PropTypes.func,
	currentShop: PropTypes.string,
	clearBooking: PropTypes.func,
	listingSEO: PropTypes.object,
	currentMerchandising: PropTypes.object,
	updateSDPFormDestination: PropTypes.func,
	destinations: PropTypes.array,
	websiteTunnels: PropTypes.array,
	productsOfMerchandising: PropTypes.array,
};

const mapStateToProps = state => {
	const currentMerchandising = state.merchandising.current;

	return {
		activeFilter: state.merchandising.activeFilter,
		resolution: state.resolution,
		partnerCode: state.partner.code,
		currentShop: state?.shop,
		merchandisingMedias: getMerchandisingMedias(state),
		currentMerchandising,
		productsOfMerchandising: getProductsOfMerchandising(state),
		merchandisings: state.merchandising.lightList,
		shouldHideProductPriceBanner: shouldMerchHideProductPriceBanner(state),
		merchandisingTopics: getMerchandisingTopicsFilterValuesFromMerchCode(state),
		fallbackRedirectUrl: getFallbackRedirectUrl(state),
		listingSEO: getPartnerListingSEO(state),
		destinations: state.smartDP.destinations,
		isPaymentDescriptionBlockExist: Boolean(getPaymentDescriptionBlock(state)),
		websiteTunnels: getWebsiteTunnels(state),
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			clearProduct,
			acceptCookyPolicy,
			fetchMerchandising,
			resetAllProductsFilter,
			updateSDPFormDestination,
			clearBooking,
			fetchSDPFilters,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(MerchandisingContainer);
